// @ts-nocheck
/** Optional consented search/cart correlation. The edge feature flag controls
 * activation. Payment is recorded only by Shopify's signed server webhook. */
const KEY = 'birch:juno:cart:v2';
const REVOKE = 'birch:juno:revoke:v2';
const CART = '_juno_cart';
const DIRECT = '_juno_direct';
const CHECKOUT = '__juno_checkout';
const nativeFetch = window.fetch.bind(window);
const root = window.Shopify?.routes?.root || '/';
const route = () => (window.BirchAI?.routes?.aiProxy || '/apps/ai').replace(/\/$/, '') + '/juno/journey';
let token = '', revision = 0, enabled = false, initPromise, queue = Promise.resolve();
const read = key => { try { return localStorage.getItem(key); } catch { return null; } };
const write = (key,value) => { try { value ? localStorage.setItem(key,value) : localStorage.removeItem(key); } catch {} };
const allowed = () => window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
const context = () => window.BirchAI?.junoTelemetry?.searchContext?.() || {};
const locked = work => navigator.locks?.request ? navigator.locks.request(KEY,work) : work();
async function post(body) {
  const res = await nativeFetch(route(), { method:'POST', credentials:'same-origin',
    headers:{'Content-Type':'application/json'},body:JSON.stringify({...context(),token,...body}),
    signal:AbortSignal.timeout(2000) });
  const data = await res.json();
  if(!res.ok) {
    if(data.revision !== undefined) revision=data.revision;
    throw new Error('attribution_' + res.status);
  }
  return data;
}
async function init() {
  if(initPromise) return initPromise;
  initPromise=(async()=>{
    const pending=read(REVOKE);
    if(pending) { await post({action:'revoke',token:pending});write(REVOKE,''); }
    await window.BirchAI?.junoTelemetry?.searchContextWhenReady?.();
    if(!allowed()) return false;
    let candidate=read(KEY)||undefined;
    if(!candidate) {
      const cart=await (await nativeFetch(root+'cart.js',{signal:AbortSignal.timeout(2000)})).json();
      const carried=[...new Set((cart.items||[]).map(i=>i.properties?.[CART]).filter(Boolean))];
      // The edge permits reuse on another device only for the verified owner.
      if(carried.length===1) candidate=carried[0];
    }
    const result=await post({action:'init',token:candidate});
    enabled=result.enabled===true;
    if(enabled) {token=result.token;revision=result.revision;write(KEY,token);}
    return enabled;
  })().catch(()=>false).finally(()=>{initPromise=undefined;});
  return initPromise;
}
const quantities = cart => (cart.items||[]).filter(i=>!i.parent_relationship?.parent && (!i.properties?.[CART] || i.properties[CART]===token))
  .map(i=>({variant_id:String(i.variant_id),quantity:i.quantity,properties:i.properties,
    selling_plan:i.selling_plan_allocation?.selling_plan?.id}));
async function sync(added=false,sourceToken) {
  if(!enabled || !allowed()) return;
  const res=await nativeFetch(root+'cart.js',{signal:AbortSignal.timeout(2000)});
  if(!res.ok) throw new Error('cart_snapshot_failed');
  const cart=await res.json();
  for(let attempt=0;attempt<2;attempt++) {
    try {
      const result=await post({action:'sync',revision,items:quantities(cart),added,source_token:sourceToken,
        preserve_missing:!!read('birch:cart:pendingRestore')});
      revision=result.revision;return;
    } catch(error) {
      if(attempt || error.message!=='attribution_409') throw error;
    }
  }
}
async function stampCheckout() {
  if(!enabled || !allowed()) return;
  const result=await post({action:'checkout'});
  const res=await nativeFetch(root+'cart/update.js',{method:'POST',headers:{'Content-Type':'application/json'},
    body:JSON.stringify({attributes:{[CHECKOUT]:result.checkout_token}}),signal:AbortSignal.timeout(2000)});
  if(!res.ok) throw new Error('checkout_carrier_failed');
}
function enqueue(work) {
  queue=queue.then(()=>locked(work)).catch(()=>{
    // No identity, token, or cart data in diagnostics.
    window.dispatchEvent(new CustomEvent('birch:juno-attribution-status',{detail:{status:'incomplete'}}));
  });
  return queue;
}
async function refresh() {
  if(!await init()) return;
  await sync(false);
  await stampCheckout();
  await forms();
}
async function forms() {
  if(!enabled || !allowed()) return;
  await Promise.all([...document.querySelectorAll('form[action*="/cart/add"]')].map(async form=>{
    const variant=form.querySelector('[name="id"]')?.value;
    if(!variant) return;
    const put=(name,value)=>{
      let input=form.querySelector(`input[name="properties[${name}]"]`);
      if(!input) {input=document.createElement('input');input.type='hidden';input.name=`properties[${name}]`;form.append(input);}
      input.value=value;
    };
    put(CART,token);
    const direct=await post({action:'checkout',direct:true,items:[{variant_id:String(variant),quantity:Number(form.querySelector('[name="quantity"]')?.value)||1}]});
    put(DIRECT,direct.checkout_token);
  }));
}
/** Intercept only first-party cart writes. Preserve Request options, non-Juno
 * attributes, sections, bundle properties and selling plans. Native wallet
 * forms use the hidden fields above and do not depend on this interceptor. */
window.fetch=async function(input,options) {
  const url=new URL(((typeof input === 'string') || (input instanceof URL)) ? input : input.url,location.href);
  const method=(options?.method||(input instanceof Request?input.method:'GET')).toUpperCase();
  if(url.origin!==location.origin || method!=='POST' || !/\/cart\/(add|change|update|clear)(\.js)?$/.test(url.pathname)) {
    const response=await nativeFetch(input,options);
    if(url.origin===location.origin && /\/apps\/ai\/search$/.test(url.pathname) && response.ok) enqueue(refresh);
    return response;
  }
  // Do not delay or modify storefront writes until the server has enabled this.
  if(!enabled || !allowed()) return nativeFetch(input,options);
  const add=/\/add(\.js)?$/.test(url.pathname);
  let initOptions=options;
  if(add && options?.body) {
    if(options.body instanceof FormData) {
      const body=new FormData(); for(const [k,v] of options.body) body.append(k,v);
      body.set(`properties[${CART}]`,token);body.delete(`properties[${DIRECT}]`);
      initOptions={...options,body};
    } else if(typeof options.body==='string') {
      try {
        const body=JSON.parse(options.body);
        for(const item of body.items||[body]) {
          item.properties={...item.properties,[CART]:item.properties?.[CART]||token};
          delete item.properties[DIRECT];
        }
        initOptions={...options,body:JSON.stringify(body)};
      } catch { /* Preserve unknown body encodings unchanged. */ }
    }
  }
  // Freeze the source when the add is requested, before later search responses.
  // Serialize writes and their snapshots so simultaneous adds cannot borrow
  // one another's quantity/source. Metadata failures still return the cart result.
  const frozen=post({action:'addition'}).catch(()=>null);
  const work=queue.then(()=>locked(async()=>{
    const response=await nativeFetch(input,initOptions);
    if(response.ok) {
      try {await sync(true,(await frozen)?.checkout_token);await stampCheckout();}
      catch {window.dispatchEvent(new CustomEvent('birch:juno-attribution-status',{detail:{status:'incomplete'}}));}
    }
    return response;
  }));
  queue=work.then(()=>{},()=>{});
  return work;
};
async function revoke() {
  const old=token||read(KEY);
  enabled=false; token='';write(KEY,'');
  if(old) write(REVOKE,old);
  for(const field of document.querySelectorAll(`input[name="properties[${CART}]"],input[name="properties[${DIRECT}]"]`)) field.remove();
  // Persist revocation intent before deleting any correlation state. Retry on
  // the next load; a fresh grant must not revive the previous token.
  if(old) {await post({action:'revoke',token:old});write(REVOKE,'');}
  await nativeFetch(root+'cart/update.js',{method:'POST',headers:{'Content-Type':'application/json'},
    body:JSON.stringify({attributes:{[CHECKOUT]:''}}),signal:AbortSignal.timeout(2000)});
}

/** The custom multi-item Buy Now already snapshots the old cart. Carry opaque
 * metadata through Ajax instead of placing a bearer token in a permalink URL.
 * This is a fresh purchase, so its context deliberately excludes retained lots. */
async function buyNow(items) {
  if(!enabled || !allowed()) return false;
  try {
    await queue;
    const direct=await post({action:'checkout',direct:true,items:items.map(i=>({variant_id:String(i.id),quantity:i.quantity,properties:i.properties,selling_plan:i.selling_plan}))});
    const clear=await nativeFetch(root+'cart/clear.js',{method:'POST',signal:AbortSignal.timeout(2000)});
    if(!clear.ok) return false;
    const add=await nativeFetch(root+'cart/add.js',{method:'POST',headers:{'Content-Type':'application/json'},
      body:JSON.stringify({items:items.map(i=>({...i,properties:{...i.properties,[CART]:token,[DIRECT]:direct.checkout_token}}))}),signal:AbortSignal.timeout(2000)});
    if(!add.ok) return false;
    const stamp=await nativeFetch(root+'cart/update.js',{method:'POST',headers:{'Content-Type':'application/json'},
      body:JSON.stringify({attributes:{[CHECKOUT]:direct.checkout_token}}),signal:AbortSignal.timeout(2000)});
    if(!stamp.ok) return false;
    location.href=root+'checkout';
    return true;
  } catch { return false; }
}
document.addEventListener('visitorConsentCollected',()=>{
  if(window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.()===false) {
    enabled=false;revoke().catch(()=>{});
  }
  else enqueue(refresh);
});
window.addEventListener('storage',event=>{if(event.key===KEY || event.key===REVOKE) enqueue(refresh);});
window.addEventListener('pageshow',()=>enqueue(refresh));
window.addEventListener('birch:turn_done',()=>enqueue(refresh));
// Combined listings replace forms and product identities without navigation.
document.addEventListener('section:update',()=>enqueue(forms));
document.addEventListener('variant:update',()=>enqueue(forms));
// Freeze a normal cart checkout before the theme's existing handlers run,
// including selective checkout. Never intercept native wallet components.
const prepared=new WeakSet();
document.addEventListener('click',async event=>{
  const button=event.target?.closest?.('#checkout,button[name="checkout"],a[href="/checkout"]');
  if(!button || !enabled || !allowed() || button.closest('shopify-accelerated-checkout,.shopify-payment-button')) return;
  if(prepared.has(button)) {prepared.delete(button);return;}
  event.preventDefault();event.stopImmediatePropagation();
  await enqueue(async()=>{await sync(false);await stampCheckout();});
  prepared.add(button);button.click();
},true);
document.addEventListener('change',event=>{
  if(event.target?.matches?.('form[action*="/cart/add"] input,form[action*="/cart/add"] select')) enqueue(forms);
});
window.BirchAI=window.BirchAI||{};
window.BirchAI.attribution={refresh:()=>enqueue(refresh),prepareCheckout:()=>enqueue(async()=>{await sync(false);await stampCheckout();}),buyNow};
enqueue(refresh);
